import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MapPin, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import { EVENTS_DATA } from '../data/events';

function PosterPreview({ src, alt, className }) {
  if (!src) {
    return (
      <div className={`w-full h-full bg-black/10 flex items-center justify-center ${className || ''}`}>
        <span className="text-white/40">No preview</span>
      </div>
    );
  }

  const isPdf = /\.pdf(\?|$)/i.test(src);
  const isDrive = src.includes('drive.google.com');

  if (isDrive) {
    const m = src.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    const preview = m ? `https://drive.google.com/file/d/${m[1]}/preview` : src.replace('/view?usp=sharing', '/preview');
    return <iframe src={preview} title={alt} className={`${className || ''}`} />;
  }

  if (isPdf) {
    return (
      <object data={src} type="application/pdf" className={`${className || ''}`}>
        <div className="w-full h-full flex items-center justify-center">
          <a href={src} target="_blank" rel="noopener noreferrer" className="text-cyan-400">Open PDF</a>
        </div>
      </object>
    );
  }

  return <img src={src} alt={alt} className={className} loading="lazy" />;
}

export default function EventDetailsPage() {
  const { eventId } = useParams();
  const dept = EVENTS_DATA.departmentEvents.find((d) => d.events.some((ev) => ev.id === eventId));
  const event = dept?.events.find((ev) => ev.id === eventId);

  if (!event) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Navbar />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 pt-28 pb-20">
          <h1 className="font-heading text-3xl text-white mb-3">Event not found</h1>
          <p className="text-white/50 mb-8">The requested event preview could not be loaded.</p>
          <Link
            to="/events"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/[0.1] text-white/80 hover:text-white hover:border-white/30 transition-colors"
          >
            <ArrowLeft size={16} /> Back to Events
          </Link>
        </main>
      </div>
    );
  }

  const registerHref = event.registerUrl || event.registrationLink;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEO
        title={`${event.name} | Ekatva 2026`}
        description={event.tagline || event.description?.slice(0, 150)}
        url={`https://ekatva-fest.smvdu.ac.in/events/${event.id}`}
        canonicalUrl={`https://ekatva-fest.smvdu.ac.in/events/${event.id}`}
      />
      <Navbar />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 pt-28 pb-20">
        <Link
          to="/events"
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-white/[0.08] text-white/70 hover:text-white hover:border-white/[0.2] transition-colors"
        >
          <ArrowLeft size={14} /> Back to Events
        </Link>

        <div className="rounded-2xl border border-white/[0.08] bg-[#0a0a0a] overflow-hidden shadow-[0_8px_60px_rgba(0,0,0,0.6)]">
          <div className="relative h-56 sm:h-72 md:h-80">
            <PosterPreview src={event.image || event.poster} alt={event.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/40 to-transparent" />
          </div>

          <div className="p-5 sm:p-7 md:p-9">
            <p className={`text-xs font-mono uppercase tracking-widest ${dept?.iconColor || 'text-cyan-400'} mb-3`}>
              {dept?.department || 'Ekatva Event'}
            </p>
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl text-white mb-2">{event.name}</h1>
            <p className={`text-sm sm:text-base font-mono bg-gradient-to-r ${dept?.gradient || 'from-cyan-400 to-blue-500'} bg-clip-text text-transparent mb-5`}>
              {event.tagline}
            </p>

            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-6">
              {event.date && (
                <div className="flex items-center gap-2">
                  <Calendar size={15} className={dept?.iconColor || 'text-cyan-400'} />
                  <span className="font-mono text-xs sm:text-sm text-white/60">{event.date}</span>
                </div>
              )}
              {event.time && (
                <div className="flex items-center gap-2">
                  <Clock size={15} className={dept?.iconColor || 'text-cyan-400'} />
                  <span className="font-mono text-xs sm:text-sm text-white/60">{event.time}</span>
                </div>
              )}
              {event.venue && (
                <div className="flex items-center gap-2">
                  <MapPin size={15} className={dept?.iconColor || 'text-cyan-400'} />
                  <span className="font-mono text-xs sm:text-sm text-white/60">{event.venue}</span>
                </div>
              )}
            </div>

            {event.description && (
              <p className="font-body text-sm sm:text-base text-white/60 leading-relaxed whitespace-pre-line mb-6">
                {event.description}
              </p>
            )}

            {event.coordinator?.name && (
              <div className="mb-4">
                <div className="font-mono text-xs text-white/40 uppercase tracking-wide">Coordinator</div>
                <div className="font-body text-sm text-white/70">{event.coordinator.name}{event.coordinator?.email ? ` • ${event.coordinator.email}` : ''}</div>
              </div>
            )}

            {event.otherDetails && (
              <div className="mb-6">
                <div className="font-mono text-xs text-white/40 uppercase tracking-wide">Other Details</div>
                <div className="font-body text-sm text-white/70 whitespace-pre-line">{event.otherDetails}</div>
              </div>
            )}

            {registerHref ? (
              <a
                href={registerHref}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-body text-sm tracking-wide bg-gradient-to-r ${dept?.gradient || 'from-cyan-400 to-blue-500'} text-white hover:opacity-90 transition-opacity`}
              >
                <ExternalLink size={14} /> Register Now
              </a>
            ) : (
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-xs text-white/30 border border-white/[0.06]">
                Registration coming soon
              </span>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
